import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "sonner";
import { Snowflake, FileDown } from "lucide-react";
import api, { errMsg } from "@/lib/api";

const EMPTY_FORM = { school_name: "", school_address: "", county: "", teacher_name: "", email: "", phone: "",
                     group_size: "", age_group: "", payment_method: "", po_number: "", notes: "" };
const EMPTY_WAITLIST = { school_name: "", teacher_name: "", email: "", phone: "", group_size: "" };

export default function SchoolBookingPage() {
  const { rink = "clonmel" } = useParams();
  const validRink = ["sligo", "clonmel"].includes(rink);
  const [config, setConfig] = useState(null);
  const [days, setDays] = useState([]);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [done, setDone] = useState(null);
  const [saving, setSaving] = useState(false);
  const [waitlistSlot, setWaitlistSlot] = useState(null);
  const [wlForm, setWlForm] = useState(EMPTY_WAITLIST);
  const [lookupRef, setLookupRef] = useState("");
  const [lookupResult, setLookupResult] = useState(undefined);

  const load = async () => {
    try {
      const [{ data: cfg }, { data: av }] = await Promise.all([
        api.get("/school/config", { params: { rink } }),
        api.get("/school/availability", { params: { rink } }),
      ]);
      setConfig(cfg);
      setDays(av.dates);
      setForm((f) => ({ ...f, payment_method: f.payment_method || (cfg.payment_methods.find((pm) => pm.value === "invoice")?.value ?? cfg.payment_methods[0]?.value ?? "") }));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  useEffect(() => { if (validRink) load(); }, [rink]);

  if (!validRink) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center font-bold">Unknown rink.</div>;
  }

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setWl = (k) => (e) => setWlForm((f) => ({ ...f, [k]: e.target.value }));
  const price = config?.price_per_child ?? 14;

  const submit = async () => {
    if (!sel) return toast.error("Please choose a session slot first");
    for (const k of ["school_name", "school_address", "county", "teacher_name", "email", "phone", "age_group"]) {
      if (!String(form[k]).trim()) return toast.error("Please fill in all required fields");
    }
    const size = Number(form.group_size);
    if (!size || size < 1 || size > 75) return toast.error("Number of pupils must be between 1 and 75");
    setSaving(true);
    try {
      const { data } = await api.post("/school/bookings",
        { ...form, group_size: size, date: sel.date, time: sel.time }, { params: { rink } });
      setDone(data);
      setSel(null);
      setForm({ ...EMPTY_FORM, payment_method: (config.payment_methods.find((pm) => pm.value === "invoice")?.value ?? config.payment_methods[0]?.value ?? "") });
      load();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const submitWaitlist = async () => {
    const size = Number(wlForm.group_size);
    if (!wlForm.school_name.trim() || !wlForm.teacher_name.trim() || !wlForm.email.trim() || !wlForm.phone.trim()) {
      return toast.error("Please fill in all waitlist fields");
    }
    if (!size || size < 1 || size > 75) return toast.error("Number of pupils must be between 1 and 75");
    try {
      await api.post("/school/waitlist", { ...wlForm, group_size: size, date: waitlistSlot.date, time: waitlistSlot.time },
        { params: { rink } });
      toast.success("You're on the waitlist — we'll email you if the slot opens up.");
      setWaitlistSlot(null);
      setWlForm(EMPTY_WAITLIST);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const lookup = async () => {
    if (!lookupRef.trim()) return;
    setLookupResult(undefined);
    try {
      const { data } = await api.get(`/school/bookings/${encodeURIComponent(lookupRef.trim())}`);
      setLookupResult(data);
    } catch {
      setLookupResult(null);
    }
  };

  const visibleDays = days.filter((d) => d.slots.some((s) => !s.blocked));

  return (
    <div className="min-h-screen bg-slate-50" data-testid="school-booking-page">
      <header className="bg-white border-b-2 border-slate-900 px-4 md:px-6 py-4 shadow-[0px_4px_0px_0px_#0f172a]">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <div className="w-10 h-10 bg-sky-500 border-2 border-slate-900 shadow-[2px_2px_0px_0px_#0f172a] flex items-center justify-center">
            <Snowflake className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-display font-extrabold uppercase text-lg leading-tight" data-testid="school-brand">{config?.label || "School Bookings"}</div>
            <div className="font-mono-tight text-[10px] uppercase tracking-[0.25em] text-slate-500">School Bookings</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 md:p-6 space-y-6">
        {done && (
          <div className="card-brutal p-6 bg-emerald-100 shadow-[6px_6px_0px_0px_#0f172a]" data-testid="booking-confirmation">
            <h2 className="font-display text-2xl uppercase mb-2">Thank you for your booking!</h2>
            <p className="text-sm mb-2">Your booking reference is:</p>
            <div className="font-mono-tight text-2xl font-bold bg-white border-2 border-slate-900 inline-block px-4 py-2 mb-3" data-testid="booking-reference">{done.reference}</div>
            <p className="text-sm text-slate-700">
              {format(new Date(`${done.date}T00:00:00`), "EEEE d MMMM yyyy")} at {done.time} — {done.school_name} ({done.group_size} pupils).
              Keep your reference safe — you can use it below to find your booking.
            </p>
            <button onClick={() => setDone(null)} className="mt-4 bg-slate-900 text-white border-2 border-slate-900 font-bold uppercase text-xs px-4 py-2 tracking-wider hover:bg-slate-700 transition-colors" data-testid="make-another-booking">
              Make another booking
            </button>
          </div>
        )}

        <div className="card-brutal p-6 shadow-[6px_6px_0px_0px_#0f172a]">
          <h1 className="font-display text-2xl md:text-3xl uppercase mb-2">School Bookings</h1>
          <p className="text-sm text-slate-600">{config?.description}</p>
        </div>

        {config?.info_pack && (
          <div className="card-brutal p-5 shadow-[6px_6px_0px_0px_#0f172a] flex items-center justify-between gap-4 flex-wrap bg-amber-100" data-testid="info-pack">
            <div>
              <div className="font-mono-tight text-xs uppercase tracking-[0.25em] text-slate-500 mb-1">Teacher resources</div>
              <p className="text-sm font-bold">School Information Pack (PDF)</p>
              <p className="text-xs text-slate-600">What to bring, supervision guidance, and what to expect on the day.</p>
            </div>
            <a href={config.info_pack} target="_blank" rel="noopener noreferrer"
              className="bg-slate-900 text-white border-2 border-slate-900 font-bold uppercase text-xs px-4 py-2.5 tracking-wider hover:bg-sky-600 transition-colors shadow-[3px_3px_0px_0px_#0f172a] flex items-center gap-2"
              data-testid="download-info-pack">
              <FileDown className="w-4 h-4" /> Download PDF
            </a>
          </div>
        )}

        <div className="card-brutal p-6 shadow-[6px_6px_0px_0px_#0f172a]" data-testid="slot-picker">
          <div className="font-mono-tight text-xs uppercase tracking-[0.25em] text-slate-500 mb-3">Step 1 — Choose your slot</div>
          <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
            {visibleDays.length === 0 && <p className="text-sm text-slate-600 font-bold">No dates currently available — please check back soon.</p>}
            {visibleDays.map((d) => (
              <div key={d.date} className="border-b border-slate-200 pb-3">
                <div className="font-bold text-sm mb-1.5">{format(new Date(`${d.date}T00:00:00`), "EEE d MMM")}</div>
                <div className="flex flex-nowrap gap-2 overflow-x-auto pb-0.5">
                {d.slots.map((s) => {
                  const selected = sel && sel.date === d.date && sel.time === s.time;
                  return (
                    <button
                      key={s.time}
                      data-testid={`slot-${d.date}-${s.time.replace(":", "")}`}
                      onClick={() => (s.available ? setSel({ date: d.date, time: s.time }) : setWaitlistSlot({ date: d.date, time: s.time }))}
                      className={`flex flex-col items-center leading-tight shrink-0 ${selected
                        ? "bg-sky-500 text-white border-2 border-slate-900 font-bold text-xs px-3 py-2 shadow-[2px_2px_0px_0px_#0f172a]"
                        : s.available
                          ? "bg-white border-2 border-slate-900 font-bold text-xs px-3 py-2 hover:bg-sky-100 shadow-[2px_2px_0px_0px_#0f172a] transition-colors"
                          : "bg-slate-200 text-slate-500 border-2 border-slate-400 text-xs px-3 py-1.5 cursor-pointer"}`}
                      title={s.available ? `${s.remaining} places left` : "Full/closed — click to join the waitlist"}
                    >
                      <span>{s.time}{selected ? " · Selected" : ""}</span>
                      {!selected && (
                        <span className="text-[9px] uppercase tracking-wider font-normal">{s.available ? "Available" : "Join Waitlist"}</span>
                      )}
                    </button>
                  );
                })}
                </div>
              </div>
            ))}
          </div>
          {sel && (
            <div className="mt-3 bg-sky-100 border-2 border-slate-900 px-3 py-2 text-sm font-bold" data-testid="selected-slot">
              Selected: {format(new Date(`${sel.date}T00:00:00`), "EEEE d MMMM yyyy")} at {sel.time}
            </div>
          )}
        </div>

        <div className="card-brutal p-6 shadow-[6px_6px_0px_0px_#0f172a]" data-testid="school-form">
          <div className="font-mono-tight text-xs uppercase tracking-[0.25em] text-slate-500 mb-3">Step 2 — Your details</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="text-sm font-bold">School name *
              <input className="input-brutal mt-1 w-full" value={form.school_name} onChange={set("school_name")} maxLength={200} data-testid="input-school-name" />
            </label>
            <label className="text-sm font-bold">County *
              <select className="input-brutal mt-1 w-full" value={form.county} onChange={set("county")} data-testid="input-county">
                <option value="">Select county…</option>
                {config?.counties.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="text-sm font-bold md:col-span-2">School address *
              <textarea className="input-brutal mt-1 w-full" rows={2} placeholder="Street, town, Eircode" value={form.school_address} onChange={set("school_address")} maxLength={500} data-testid="input-school-address" />
            </label>
            <label className="text-sm font-bold">Teacher / contact name *
              <input className="input-brutal mt-1 w-full" value={form.teacher_name} onChange={set("teacher_name")} maxLength={200} data-testid="input-teacher-name" />
            </label>
            <label className="text-sm font-bold">Email *
              <input type="email" className="input-brutal mt-1 w-full" value={form.email} onChange={set("email")} data-testid="input-email" />
            </label>
            <label className="text-sm font-bold">Phone *
              <input className="input-brutal mt-1 w-full" value={form.phone} onChange={set("phone")} maxLength={40} data-testid="input-phone" />
            </label>
            <label className="text-sm font-bold">Number of pupils * (max 75)
              <input type="number" min="1" max="75" className="input-brutal mt-1 w-full" value={form.group_size} onChange={set("group_size")} data-testid="input-group-size" />
            </label>
            <label className="text-sm font-bold">Age group / class *
              {config?.age_groups ? (
                <select className="input-brutal mt-1 w-full" value={form.age_group} onChange={set("age_group")} data-testid="input-age-group">
                  <option value="">Select age group…</option>
                  {config.age_groups.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              ) : (
                <input className="input-brutal mt-1 w-full" placeholder="e.g. Junior Infants, 3rd Class" value={form.age_group} onChange={set("age_group")} maxLength={100} data-testid="input-age-group" />
              )}
            </label>
            {config?.has_po_number && (
              <label className="text-sm font-bold">PO number (optional)
                <input className="input-brutal mt-1 w-full" value={form.po_number} onChange={set("po_number")} maxLength={100} data-testid="input-po-number" />
              </label>
            )}
            <div className="text-sm font-bold md:col-span-2">Payment method *
              <div className="flex flex-wrap gap-3 mt-1">
                {config?.payment_methods.map((pm) => (
                  <label key={pm.value} className={`flex items-center gap-2 border-2 border-slate-900 px-3 py-2 cursor-pointer ${form.payment_method === pm.value ? "bg-sky-500 text-white" : "bg-white"}`}>
                    <input type="radio" name="payment_method" checked={form.payment_method === pm.value} onChange={() => setForm((f) => ({ ...f, payment_method: pm.value }))} data-testid={`payment-${pm.value}`} />
                    <span className="text-xs font-bold">{pm.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <label className="text-sm font-bold md:col-span-2">Notes (optional — skill level, SNAs, accessibility)
              <textarea className="input-brutal mt-1 w-full" rows={2} value={form.notes} onChange={set("notes")} maxLength={2000} data-testid="input-notes" />
            </label>
          </div>
          <button onClick={submit} disabled={saving} className="mt-4 w-full bg-slate-900 text-white border-2 border-slate-900 font-extrabold uppercase text-sm px-4 py-3 tracking-wider hover:bg-sky-600 transition-colors shadow-[4px_4px_0px_0px_#0f172a] disabled:opacity-50" data-testid="submit-booking">
            {saving ? "Booking…" : `Book session${form.group_size ? ` — €${(Number(form.group_size) * price).toFixed(2)}` : ""}`}
          </button>
        </div>

        <div className="card-brutal p-6 shadow-[6px_6px_0px_0px_#0f172a]" data-testid="lookup-section">
          <div className="font-mono-tight text-xs uppercase tracking-[0.25em] text-slate-500 mb-3">Find my booking</div>
          <div className="flex gap-2">
            <input className="input-brutal flex-1" placeholder={`e.g. ${rink === "sligo" ? "SOI" : "COI"}-1A2B3C4D`} value={lookupRef}
              onChange={(e) => setLookupRef(e.target.value)} onKeyDown={(e) => e.key === "Enter" && lookup()} data-testid="lookup-input" />
            <button onClick={lookup} className="bg-slate-900 text-white border-2 border-slate-900 font-bold uppercase text-xs px-4 tracking-wider hover:bg-slate-700 transition-colors" data-testid="lookup-btn">Find</button>
          </div>
          {lookupResult === null && <p className="text-sm text-red-700 font-bold mt-3" data-testid="lookup-not-found">No booking found with that reference.</p>}
          {lookupResult && (
            <div className="mt-3 border-2 border-slate-900 bg-sky-50 p-4 text-sm" data-testid="lookup-result">
              <div className="font-bold">{lookupResult.school_name} — {lookupResult.reference}</div>
              <div>{format(new Date(`${lookupResult.date}T00:00:00`), "EEEE d MMMM yyyy")} at {lookupResult.time} · {lookupResult.group_size} pupils · {lookupResult.age_group}</div>
              <div>{lookupResult.teacher_name} · {lookupResult.email} · {lookupResult.phone}</div>
              <div className="mt-1 uppercase font-mono-tight text-xs font-bold">Status: {lookupResult.status}</div>
            </div>
          )}
        </div>
      </main>

      {waitlistSlot && (
        <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center p-4 z-50" data-testid="waitlist-modal">
          <div className="card-brutal bg-white p-6 w-full max-w-md shadow-[6px_6px_0px_0px_#0f172a]">
            <h3 className="font-display text-xl uppercase mb-1">Join the waitlist</h3>
            <p className="text-sm text-slate-600 mb-4">
              {format(new Date(`${waitlistSlot.date}T00:00:00`), "EEEE d MMMM yyyy")} at {waitlistSlot.time} is full or closed.
              Leave your details and we'll email you if it opens up.
            </p>
            <div className="space-y-3">
              <input className="input-brutal w-full" placeholder="School name *" value={wlForm.school_name} onChange={setWl("school_name")} data-testid="wl-school-name" />
              <input className="input-brutal w-full" placeholder="Teacher name *" value={wlForm.teacher_name} onChange={setWl("teacher_name")} data-testid="wl-teacher-name" />
              <input type="email" className="input-brutal w-full" placeholder="Email *" value={wlForm.email} onChange={setWl("email")} data-testid="wl-email" />
              <input className="input-brutal w-full" placeholder="Phone *" value={wlForm.phone} onChange={setWl("phone")} data-testid="wl-phone" />
              <input type="number" min="1" max="75" className="input-brutal w-full" placeholder="Number of pupils *" value={wlForm.group_size} onChange={setWl("group_size")} data-testid="wl-group-size" />
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={submitWaitlist} className="flex-1 bg-slate-900 text-white border-2 border-slate-900 font-bold uppercase text-xs px-4 py-2 tracking-wider hover:bg-slate-700 transition-colors" data-testid="wl-submit">Join waitlist</button>
              <button onClick={() => setWaitlistSlot(null)} className="bg-white border-2 border-slate-900 font-bold uppercase text-xs px-4 py-2 tracking-wider hover:bg-slate-100 transition-colors" data-testid="wl-cancel">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
